"use client";

import { ChevronDown } from "lucide-react";
import { usePathname, useRouter } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { useEffect, useId, useRef, useState } from "react";
import { setClientCookie } from "@/lib/cookies/client";
import { type Locale, supportedLocales } from "@/lib/i18n/config";
import { LocaleFlag } from "./locale-flag";

const localeLabels: Record<Locale, string> = {
	id: "ID",
	en: "EN",
};

const localeNames: Record<Locale, string> = {
	id: "Bahasa Indonesia",
	en: "English",
};

type LanguageSwitcherProps = {
	locale: Locale;
	variant?: "compact" | "menu";
};

export function LanguageSwitcher({
	locale,
	variant = "compact",
}: LanguageSwitcherProps) {
	const pathname = usePathname();
	const router = useRouter();
	const posthog = usePostHog();
	const [open, setOpen] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);
	const menuId = useId();

	useEffect(() => {
		if (!open) return;
		function handleClickOutside(event: MouseEvent) {
			if (
				containerRef.current &&
				!containerRef.current.contains(event.target as Node)
			) {
				setOpen(false);
			}
		}
		function handleKey(event: KeyboardEvent) {
			if (event.key === "Escape") setOpen(false);
		}
		document.addEventListener("mousedown", handleClickOutside);
		document.addEventListener("keydown", handleKey);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
			document.removeEventListener("keydown", handleKey);
		};
	}, [open]);

	function switchLocale(newLocale: Locale) {
		setOpen(false);
		if (newLocale === locale) return;

		posthog?.capture("locale_switched", {
			from_locale: locale,
			to_locale: newLocale,
			variant,
		});

		setClientCookie("locale", newLocale, {
			sameSite: "Lax",
			maxAgeSeconds: 60 * 60 * 24 * 365, // 1 year
		});

		const segments = pathname.split("/");
		segments[1] = newLocale;
		// segments[1] is the (non-empty) locale, so the join is never empty —
		// "" → "/en", "/" → "/en", "/id/pricing" → "/en/pricing".
		router.push(segments.join("/"));
	}

	const isMenuVariant = variant === "menu";

	return (
		<div
			ref={containerRef}
			className={`relative inline-flex items-center ${
				isMenuVariant ? "shrink-0" : ""
			}`}
		>
			<button
				type="button"
				onClick={() => setOpen((prev) => !prev)}
				aria-haspopup="true"
				aria-expanded={open}
				aria-controls={menuId}
				aria-label={`${localeLabels[locale]} — Switch language`}
				className={`inline-flex cursor-pointer items-center gap-1.5 rounded-2xl border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
					isMenuVariant
						? "min-h-11 border-border/60 bg-background px-3 py-2 text-sm font-medium text-foreground hover:bg-accent"
						: "h-11 border-border/70 bg-card px-3 py-2 text-sm font-medium text-foreground hover:bg-accent"
				}`}
			>
				<LocaleFlag locale={locale} className="h-5 w-5" />
				<span className="text-xs font-semibold uppercase tracking-wide">
					{localeLabels[locale]}
				</span>
				<ChevronDown
					className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${
						open ? "rotate-180" : ""
					}`}
					aria-hidden="true"
				/>
			</button>

			{open ? (
				<div
					id={menuId}
					data-testid="language-switcher-panel"
					className="absolute right-0 top-full z-50 mt-2 min-w-[10rem] overflow-hidden rounded-2xl border border-border/70 bg-popover p-1 shadow-lg"
				>
					{supportedLocales.map((loc) => {
						const isActive = loc === locale;
						return (
							<button
								key={loc}
								type="button"
								aria-current={isActive ? "true" : undefined}
								onClick={() => switchLocale(loc)}
								className={`flex w-full cursor-pointer items-center gap-2.5 rounded-xl px-3 py-2 text-left text-sm transition-colors ${
									isActive
										? "bg-accent font-semibold text-foreground"
										: "text-foreground hover:bg-accent"
								}`}
							>
								<LocaleFlag locale={loc} className="h-5 w-5" />
								<span className="flex-1">{localeNames[loc]}</span>
								<span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
									{localeLabels[loc]}
								</span>
							</button>
						);
					})}
				</div>
			) : null}
		</div>
	);
}
